
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, AlertCircle, Lock, Clock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useFaturaLock } from '../../src/hooks/useFaturaLock';
import { useAuth } from '../../src/contexts/AuthContext';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { ehErroDuplicataFatura } from '../../src/lib/duplicataFatura';
import ConfirmarDuplicataModal from './ConfirmarDuplicataModal';

interface FaturaModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura?: any;
    onSave: () => void;
}

const FaturaModal: React.FC<FaturaModalProps> = ({ isOpen, onClose, fatura, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Duplicata: o banco recusa a segunda fatura do mesmo mês/UC, e aí perguntamos
    // se ela é legítima em vez de mostrar o erro cru.
    const [duplicata, setDuplicata] = useState<{ uc: string; mes: string } | null>(null);
    const prevIsOpenRef = useRef(false);

    const { user } = useAuth();
    const faturaId = fatura?.id ? Number(fatura.id) : null;
    const { isMyLock, lockInfo, acquiring, acquireLock, releaseLock } = useFaturaLock(faturaId, user?.id);
    const [lockChecked, setLockChecked] = useState(false);
    const prevIsOpenLockRef = useRef(false);

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenLockRef.current;
        const justClosed = !isOpen && prevIsOpenLockRef.current;
        prevIsOpenLockRef.current = isOpen;

        if (justOpened) {
            setLockChecked(false);
            acquireLock().then(() => setLockChecked(true));
        }
        if (justClosed) {
            releaseLock();
            setLockChecked(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleClose = useCallback(async () => {
        await releaseLock();
        onClose();
    }, [releaseLock, onClose]);

    const [formData, setFormData] = useState({
        uc_codigo: '',
        cliente_nome: '',
        cliente_cpf_cnpj: '',
        tipo_ligacao: 'Trifásico',
        mes_referencia: '',
        data_vencimento: '',
        valor_fatura_distribuidora: '',
        valor_tarifa_distribuidora: '',
        consumo_ativo_kwh: '',

        // GD Fields
        energia_injetada_gd1_kwh: '',
        energia_injetada_gd2_kwh: '',
        tarifa_injecao_gd1: '',
        tarifa_injecao_gd2: '',
        tarifa_bandeira: '',
        consumo_nao_comp: '',
        ilum_publica: '',
        energia_nao_isenta: '',
        outros_multas: '',
        tarifa_consumo_s_imposto: '',
        cadastro_rateio: '',

        // Taxes
        icms: '',
        pis: '',
        cofins: ''
    });

    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;
        if (!isOpen || !justOpened) return;

        if (fatura) {
            setFormData({
                uc_codigo: fatura.uc_codigo || '',
                cliente_nome: fatura.cliente_nome || '',
                cliente_cpf_cnpj: fatura.cliente_cpf_cnpj || '',
                tipo_ligacao: (fatura.tipo_ligacao || '').toLowerCase().includes('mono') ? 'Monofásico' : (fatura.tipo_ligacao || '').toLowerCase().includes('bif') ? 'Bifásico' : 'Trifásico',
                mes_referencia: fatura.mes_referencia || '',
                data_vencimento: fatura.data_vencimento ? fatura.data_vencimento.split('T')[0] : '',
                valor_fatura_distribuidora: fatura.valor_fatura_distribuidora?.toString() || '',
                valor_tarifa_distribuidora: fatura.valor_tarifa_distribuidora?.toString() || '',
                consumo_ativo_kwh: fatura.consumo_ativo_kwh?.toString() || '',

                energia_injetada_gd1_kwh: fatura.energia_injetada_gd1_kwh?.toString() || '',
                energia_injetada_gd2_kwh: fatura.energia_injetada_gd2_kwh?.toString() || '',
                tarifa_injecao_gd1: fatura.tarifa_injecao_gd1?.toString() || '',
                tarifa_injecao_gd2: fatura.tarifa_injecao_gd2?.toString() || '',
                tarifa_bandeira: fatura.tarifa_bandeira?.toString() || '',
                consumo_nao_comp: fatura.consumo_nao_comp?.toString() || '',
                ilum_publica: fatura.ilum_publica?.toString() || '',
                energia_nao_isenta: fatura.energia_nao_isenta?.toString() || '',
                outros_multas: fatura.outros_multas?.toString() || '',
                tarifa_consumo_s_imposto: fatura.tarifa_consumo_s_imposto?.toString() || '',
                cadastro_rateio: fatura.cadastro_rateio?.toString() || '',

                icms: fatura.icms?.toString() || '',
                pis: fatura.pis?.toString() || '',
                cofins: fatura.cofins?.toString() || ''
            });
        } else {
            // Reset or defaults
            setFormData({
                uc_codigo: '',
                cliente_nome: '',
                cliente_cpf_cnpj: '',
                tipo_ligacao: 'Trifásico',
                mes_referencia: '',
                data_vencimento: '',
                valor_fatura_distribuidora: '',
                valor_tarifa_distribuidora: '',
                consumo_ativo_kwh: '',
                energia_injetada_gd1_kwh: '',
                energia_injetada_gd2_kwh: '',
                tarifa_injecao_gd1: '',
                tarifa_injecao_gd2: '',
                tarifa_bandeira: '',
                consumo_nao_comp: '',
                ilum_publica: '',
                energia_nao_isenta: '',
                outros_multas: '',
                tarifa_consumo_s_imposto: '',
                cadastro_rateio: '',
                icms: '',
                pis: '',
                cofins: ''
            });
        }
    }, [fatura, isOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await salvarFatura();
    };

    const salvarFatura = async () => {
        setLoading(true);
        setError(null);

        try {
            const payload = {
                ...formData,
                valor_fatura_distribuidora: parseFloat(formData.valor_fatura_distribuidora) || 0,
                valor_tarifa_distribuidora: parseFloat(formData.valor_tarifa_distribuidora) || 0,
                consumo_ativo_kwh: parseFloat(formData.consumo_ativo_kwh) || 0,
                energia_injetada_gd1_kwh: parseFloat(formData.energia_injetada_gd1_kwh) || 0,
                energia_injetada_gd2_kwh: parseFloat(formData.energia_injetada_gd2_kwh) || 0,
                tarifa_injecao_gd1: parseFloat(formData.tarifa_injecao_gd1) || 0,
                tarifa_injecao_gd2: parseFloat(formData.tarifa_injecao_gd2) || 0,
                tarifa_bandeira: parseFloat(formData.tarifa_bandeira) || 0,
                consumo_nao_comp: parseFloat(formData.consumo_nao_comp) || 0,
                ilum_publica: parseFloat(formData.ilum_publica) || 0,
                energia_nao_isenta: parseFloat(formData.energia_nao_isenta) || 0,
                outros_multas: parseFloat(formData.outros_multas) || 0,
                tarifa_consumo_s_imposto: parseFloat(formData.tarifa_consumo_s_imposto) || 0,
                cadastro_rateio: parseFloat(formData.cadastro_rateio) || 0,
                icms: parseFloat(formData.icms) || 0,
                pis: parseFloat(formData.pis) || 0,
                cofins: parseFloat(formData.cofins) || 0,
            };

            if (fatura?.id) {
                const { error: updateError } = await supabase
                    .from('faturas')
                    .update(payload)
                    .eq('id', fatura.id);
                if (updateError) throw updateError;
            } else {
                const { error: insertError } = await supabase
                    .from('faturas')
                    .insert([payload]);
                if (insertError) throw insertError;
            }

            // Log Action
            if (fatura?.id) {
                await logAction(user?.id, 'UPDATE_FATURA', {
                    fatura_id: fatura.id,
                    cliente: formData.cliente_nome,
                    uc: formData.uc_codigo
                });
                logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'atualizar_fatura', origem: 'acao' });
            } else {
                await logAction(user?.id, 'CREATE_FATURA', {
                    cliente: formData.cliente_nome,
                    uc: formData.uc_codigo
                });
                logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'criar_fatura', origem: 'acao' });
            }

            await releaseLock();
            onSave();
            onClose();
        } catch (err: any) {
            console.error('Error saving fatura:', err);
            // A distribuidora às vezes emite duas faturas no mesmo mês. Em vez do erro
            // cru, pergunta se a segunda é legítima e registra o motivo.
            if (ehErroDuplicataFatura(err) && !fatura?.id) {
                setDuplicata({ uc: formData.uc_codigo, mes: formData.mes_referencia });
            } else {
                setError(err.message || 'Erro ao salvar fatura.');
            }
        } finally {
            setLoading(false);
        }
    };



    return createPortal(
        <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm bg-black/30 ${!isOpen ? 'hidden' : ''}`} {...propsFundoModal(() => handleClose())}>
            <div className="bg-white dark:bg-gray-800/95 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in border dark:border-gray-700" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-700 flex items-center justify-between p-6 z-10">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                            {fatura ? 'Editar Fatura' : 'Nova Fatura Manual'}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Preencha os dados da fatura da concessionária</p>
                    </div>
                    <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Lock screen — shown while checking or when locked by another user */}
                {fatura && (!lockChecked || (lockChecked && !isMyLock && lockInfo)) && (
                    <div className="flex flex-col items-center justify-center py-16 px-8 text-center gap-6">
                        {acquiring || !lockChecked ? (
                            <>
                                <div className="w-12 h-12 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm">Verificando disponibilidade...</p>
                            </>
                        ) : (
                            <>
                                <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                                    <Lock size={28} className="text-orange-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">Fatura em edição</h3>
                                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                                        <span className="font-semibold text-gray-700 dark:text-gray-200">{lockInfo.lockedByName}</span> está editando esta fatura no momento.
                                    </p>
                                    {lockInfo.lockedSince && (
                                        <p className="flex items-center justify-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 mt-2">
                                            <Clock size={12} />
                                            Desde {new Date(lockInfo.lockedSince).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="px-6 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Fechar
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Body — only shown when we own the lock (or it's a new fatura with no id) */}
                {(!fatura || (lockChecked && isMyLock)) && <form onSubmit={handleSubmit} className="p-6 space-y-8">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm">
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    {/* DADOS DO CLIENTE */}
                    <section>
                        <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4 border-b dark:border-gray-700 pb-2">Dados do Cliente</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Código UC <span className="text-red-500">*</span></label>
                                <input name="uc_codigo" required value={formData.uc_codigo} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nome do Cliente <span className="text-red-500">*</span></label>
                                <input name="cliente_nome" required value={formData.cliente_nome} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">CPF ou CNPJ</label>
                                <input name="cliente_cpf_cnpj" value={formData.cliente_cpf_cnpj} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Tipo de Ligação</label>
                                <select name="tipo_ligacao" value={formData.tipo_ligacao} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white">
                                    <option value="Monofásico">Monofásico</option>
                                    <option value="Bifásico">Bifásico</option>
                                    <option value="Trifásico">Trifásico</option>
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Mês Referência <span className="text-red-500">*</span></label>
                                <input name="mes_referencia" required placeholder="Ex: JAN/2024" value={formData.mes_referencia} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Vencimento <span className="text-red-500">*</span></label>
                                <input type="date" name="data_vencimento" required value={formData.data_vencimento} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-gray-900 dark:text-white dark:[color-scheme:dark]" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Valor Total (R$) <span className="text-red-500">*</span></label>
                                <input type="number" step="0.01" name="valor_fatura_distribuidora" required value={formData.valor_fatura_distribuidora} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-primary" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Valor Tarifa (Concessionária)</label>
                                <input type="number" step="0.00001" name="valor_tarifa_distribuidora" value={formData.valor_tarifa_distribuidora} onChange={handleChange} className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-medium text-primary" />
                            </div>
                        </div>
                    </section>

                    {/* DADOS DA TABELA */}
                    <section>
                        <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4 border-b dark:border-gray-700 pb-2">Dados da Tabela e Consumo</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Consumo Ativo (kWh)</label>
                                <input type="number" step="0.01" name="consumo_ativo_kwh" value={formData.consumo_ativo_kwh} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Energia Inj. GD1 (kWh)</label>
                                <input type="number" step="0.01" name="energia_injetada_gd1_kwh" value={formData.energia_injetada_gd1_kwh} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Energia Inj. GD2 (kWh)</label>
                                <input type="number" step="0.01" name="energia_injetada_gd2_kwh" value={formData.energia_injetada_gd2_kwh} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Consumo Ñ Compensado</label>
                                <input type="number" step="0.01" name="consumo_nao_comp" value={formData.consumo_nao_comp} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>


                            <div className="md:col-span-4 border-t border-gray-100 dark:border-gray-700 my-2"></div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tarifa Injeção GD1</label>
                                <input type="number" step="0.00001" name="tarifa_injecao_gd1" value={formData.tarifa_injecao_gd1} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tarifa Injeção GD2</label>
                                <input type="number" step="0.00001" name="tarifa_injecao_gd2" value={formData.tarifa_injecao_gd2} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tarifa Bandeira</label>
                                <input type="number" step="0.00001" name="tarifa_bandeira" value={formData.tarifa_bandeira} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Tarifa Consumo s/ Imp.</label>
                                <input type="number" step="0.00001" name="tarifa_consumo_s_imposto" value={formData.tarifa_consumo_s_imposto} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>

                            <div className="md:col-span-4 border-t border-gray-100 dark:border-gray-700 my-2"></div>

                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Ilum. Pública</label>
                                <input type="number" step="0.01" name="ilum_publica" value={formData.ilum_publica} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Energia não isenta</label>
                                <input type="number" step="0.01" name="energia_nao_isenta" value={formData.energia_nao_isenta} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Multas / Outros</label>
                                <input type="number" step="0.01" name="outros_multas" value={formData.outros_multas} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">Cadastro Rateio (%)</label>
                                <input type="number" step="0.01" name="cadastro_rateio" value={formData.cadastro_rateio} onChange={handleChange} className="w-full px-3 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-primary dark:focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all font-medium text-gray-900 dark:text-white" />
                            </div>

                        </div>
                    </section>

                    {/* IMPOSTOS */}
                    <section className="bg-gray-50 dark:bg-white/5 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                        <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">Impostos (%)</h3>
                        <div className="grid grid-cols-3 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">ICMS</label>
                                <input type="number" step="0.01" name="icms" value={formData.icms} onChange={handleChange} className="w-full px-4 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-mono text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">PIS</label>
                                <input type="number" step="0.01" name="pis" value={formData.pis} onChange={handleChange} className="w-full px-4 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-mono text-gray-900 dark:text-white" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">COFINS</label>
                                <input type="number" step="0.01" name="cofins" value={formData.cofins} onChange={handleChange} className="w-full px-4 py-2 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:bg-white dark:focus:bg-gray-700 focus:border-primary dark:focus:border-primary transition-all font-mono text-gray-900 dark:text-white" />
                            </div>
                        </div>
                    </section>

                </form>}

                {/* Footer — only shown when we own the lock (or it's a new fatura) */}
                {(!fatura || (lockChecked && isMyLock)) && <div className="sticky bottom-0 bg-white dark:bg-gray-800/80 backdrop-blur-md border-t border-gray-100 dark:border-gray-700 p-6 flex justify-end gap-3 z-10">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className={`flex items-center gap-2 px-8 py-2.5 rounded-lg bg-primary text-white font-bold hover:bg-primary-dark transition-all transform active:scale-95 shadow-lg shadow-primary/20 ${loading ? 'opacity-70 cursor-wait' : ''
                            }`}
                    >
                        <Save size={18} />
                        {loading ? 'Salvando...' : 'Salvar Fatura'}
                    </button>
                </div>}
            </div>

            {duplicata && (
                <ConfirmarDuplicataModal
                    ucInicial={duplicata.uc}
                    mesInicial={duplicata.mes}
                    origem="manual"
                    onClose={() => setDuplicata(null)}
                    onAutorizado={() => {
                        setDuplicata(null);
                        // Com a autorização registrada, o mesmo INSERT passa pelo banco.
                        salvarFatura();
                    }}
                />
            )}
        </div>,
        document.body
    );
};

export default FaturaModal;
